


import React, { useEffect, useState } from "react";
import { Box, Switch, Typography, CircularProgress, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import toast from "react-hot-toast";
import back from "../../../assets/back.svg";
import CustomText, { textStyles } from '../../../components/CustomText';
import { useTranslation } from "react-i18next";

const AdminSettings = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [employmentAutomation, setEmploymentAutomation] = useState(false);
  const [payslipAutomation, setPayslipAutomation] = useState(false);
  const [employmentTrigger, setEmploymentTrigger] = useState("manual");
  const [payslipTrigger, setPayslipTrigger] = useState("monthly");
  const [employmentSaving, setEmploymentSaving] = useState(false);
  const [payslipSaving, setPayslipSaving] = useState(false);

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { t } = useTranslation("");
const fetchSettings = async () => {
  try {
    setLoading(true);
    const res = await fetch(`${BASE_URL}/documents/automated/settings`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
    });
    const result = await res.json();

    if (!result.error && result.data?.settings) {
      const emp = result.data.settings.find((s) => s.document_type === "employment_certificate");
      const pay = result.data.settings.find((s) => s.document_type === "payslip");

      setEmploymentAutomation(emp?.automation_enabled || false);
      setPayslipAutomation(pay?.automation_enabled || false);
      setEmploymentTrigger(emp?.trigger_event || "manual");
      setPayslipTrigger(pay?.trigger_event || "monthly");
    }
  } catch (err) {
    console.error("Error fetching automation settings:", err);
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  fetchSettings();
}, [decryptedToken]);


  // global automation toggle
  const handleGlobalAutomationToggle = () => {
    const newState = !(employmentAutomation && payslipAutomation);
    setEmploymentAutomation(newState);
    setPayslipAutomation(newState);
  };

const handleSave = async (type) => {
  try {
    if (type === "employment_certificate") setEmploymentSaving(true);
    if (type === "payslip") setPayslipSaving(true);

    // if either is updated, reflect automation status on both
    const newAutomationState =
      type === "employment_certificate" ? employmentAutomation : payslipAutomation;

    // keep both in sync
    setEmploymentAutomation(newAutomationState);
    setPayslipAutomation(newAutomationState);

    const body =
      type === "employment_certificate"
        ? {
            document_type: "employment_certificate",
            automation_enabled: newAutomationState,
            auto_send_enabled: newAutomationState,
            trigger_event: employmentTrigger,
          }
        : {
            document_type: "payslip",
            automation_enabled: newAutomationState,
            auto_send_enabled: newAutomationState,
            trigger_event: payslipTrigger,
          };

    // log request body
    console.log("Sending body to API:", body);

    const res = await fetch(`${BASE_URL}/documents/automated/settings`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
      body: JSON.stringify(body),
    });

      const result = await res.json();
    console.log("API response:", result);

    // show toast from API message
    if (result && !result.error && result.message) {
      toast.success(result.message);
        await fetchSettings(); // refresh updated data

    } else if (result && result.error && result.message) {
      toast.error(result.message);
    } else {
      toast.success(`${type.replace("_", " ")} settings saved successfully.`);
    }
  } catch (err) {
    console.error("Error saving settings:", err);
    toast.error("Error saving settings");
  } finally {
    if (type === "employment_certificate") setEmploymentSaving(false);
    if (type === "payslip") setPayslipSaving(false);
  }
};


  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <CircularProgress color="primary" />
      </Box>
    );

  return (
    <Box sx={{ p: 4, backgroundColor: "#f8f9fb", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", mb: 3 }} onClick={onBack}>
        <img src={back} style={{ width: 30, height: 30 }} />
        <CustomText sx={{ ...textStyles.h1, fontWeight: 500, fontSize: "14px", color: "#626F86", textAlign: "center" }}>
          {t("settings.name")}
        </CustomText>
      </Box>

      {/* Global automation switch */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            color: "#172B4D",
            fontFamily: "Poppins, sans-serif",
          }}
        >
         {t("adminSettings.automation.enable")}
        </Typography>
        <Switch
          checked={employmentAutomation && payslipAutomation}
          onChange={handleGlobalAutomationToggle}
          color="primary"
        />
      </Box>

      {/* Employment Certificate */}
      <Box sx={{ backgroundColor: "white", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", p: 3, mb: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#172B4D", mb: 2, fontFamily: "Poppins, sans-serif" }}>
        {t("adminSettings.employment.title")}
        </Typography>

      

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1 }}>
          <Typography sx={{ fontSize: 14, color: "#5E5C5C", fontWeight: 500, fontFamily: "Poppins, sans-serif" }}>
           {t("adminSettings.employment.event_trigger")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontSize: 13,
                color: employmentTrigger === "on_approval" ? "#1976d2" : "#5E5C5C",
                fontWeight: employmentTrigger === "on_approval" ? 600 : 400,
                fontFamily: "Poppins, sans-serif",
              }}
            >
            {t("adminSettings.on_approval")}
            </Typography>
            <Switch
              checked={employmentTrigger === "on_approval"}
              onChange={() =>
                setEmploymentTrigger((prev) => (prev === "on_approval" ? "manual" : "on_approval"))
              }
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave("employment_certificate")}
            disabled={employmentSaving}
            sx={{ borderRadius: "6px", textTransform: "none", fontFamily: "Poppins, sans-serif" }}
          >
            {employmentSaving ? t("adminSettings.saving") : t("adminSettings.save_settings")}
          </Button>
        </Box>
      </Box>

      {/* Payslip */}
      <Box sx={{ backgroundColor: "white", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", p: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#172B4D", mb: 2, fontFamily: "Poppins, sans-serif" }}>
          {t("adminSettings.payslip.title")}

        </Typography>

     

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1 }}>
          <Typography sx={{ fontSize: 14, color: "#5E5C5C", fontWeight: 500, fontFamily: "Poppins, sans-serif" }}>
{t("adminSettings.employment.event_trigger")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: payslipTrigger === "monthly" ? 600 : 400,
                color: payslipTrigger === "monthly" ? "#1976d2" : "#5E5C5C",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("adminSettings.payslip.monthly")}

            </Typography>
            <Switch
              checked={payslipTrigger === "on_approval"}
              onChange={() =>
                setPayslipTrigger((prev) => (prev === "on_approval" ? "monthly" : "on_approval"))
              }
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: payslipTrigger === "on_approval" ? 600 : 400,
                color: payslipTrigger === "on_approval" ? "#1976d2" : "#5E5C5C",
                fontFamily: "Poppins, sans-serif",
              }}
            >
            {t("adminSettings.on_approval")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave("payslip")}
            disabled={payslipSaving}
            sx={{ borderRadius: "6px", textTransform: "none", fontFamily: "Poppins, sans-serif" }}
          >
            {payslipSaving ? t("adminSettings.saving") : t("adminSettings.save_settings")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminSettings;


































